import { useState } from 'react';
import { HiChevronDown, HiExternalLink } from 'react-icons/hi';
import Button from '../components/ui/Button';
import HeroSection from '../components/ui/HeroSection';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import siteContent from '../data/siteContent.json';
import services from '../data/services.json';
import specialEventsBg from '../assets/images/special-events-bg.png';
import servicesBg from '../assets/images/services-banner.png';
import Seo from '../components/Seo';

function ServiceDropdown({ title, defaultOpen = false, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-dusty-rose/30 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left bg-charcoal/50 hover:bg-charcoal/70 transition-colors"
      >
        <span className="text-xl md:text-2xl font-headline font-semibold text-chalk uppercase tracking-wider">
          {title}
        </span>
        <HiChevronDown
          className={`h-6 w-6 text-dusty-rose transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="p-5 pt-2 bg-charcoal/30">
          {children}
        </div>
      )}
    </div>
  );
}

function PriceRow({ name, price, priceDisplay, description, included, perPerson }) {
  return (
    <li className="flex justify-between items-start py-2 border-b border-dusty-rose/20">
      <div className="flex-1">
        <span className="font-display font-semibold text-chalk">{name}</span>
        {description && (
          <p className="text-sm text-dusty-rose/70 mt-1">{description}</p>
        )}
      </div>
      <span className="text-dusty-rose font-semibold ml-4">
        {included ? 'Included' : priceDisplay ? priceDisplay : price ? `$${price}${perPerson ? '/person' : ''}` : 'Inquire for pricing'}
      </span>
    </li>
  );
}

export default function Services() {
  return (
    <>
      <Seo
        title="Bridal Hair & Makeup Services & Pricing"
        description="Bridal hair, makeup, bridal party styling, brows, lashes, and spray tanning in Kansas City. View Blush & Bobby's wedding beauty services and pricing."
        path="/services"
      />
      {/* Hero */}
      <HeroSection title="Services" backgroundImage={servicesBg} opacity={0.8} overlayColor="bg-dusty-rose/[.07]" />

      {/* Services Dropdowns */}
      <ChalkboardBackground className="py-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Bridal Dropdown */}
            <ServiceDropdown title="Bridal" defaultOpen={true}>
              <ul className="space-y-4 text-lg">
                {services.bridal.map((service) => (
                  <PriceRow
                    key={service.id}
                    name={service.name}
                    price={service.startingAt}
                    included={service.included}
                  />
                ))}
              </ul>

              <h3 className="text-lg font-headline font-semibold text-chalk uppercase tracking-wider mt-6 mb-3">
                Bridal Party
              </h3>
              <ul className="space-y-4 text-lg">
                {services.bridalParty.map((service) => (
                  <PriceRow
                    key={service.id}
                    name={service.name}
                    price={service.startingAt}
                    description={
                      service.id === 'bridal-party-hair' || service.id === 'bridal-party-makeup'
                        ? 'Includes bridesmaids, family members, and all party members over junior age'
                        : undefined
                    }
                    perPerson={service.perPerson}
                  />
                ))}
              </ul>
            </ServiceDropdown>

            {/* Brows + Lashes Dropdown */}
            <ServiceDropdown title="Brows + Lashes">
              <ul className="space-y-4 text-lg">
                {services.browsLashes.map((service) => (
                  <PriceRow
                    key={service.id}
                    name={service.name}
                    price={service.startingAt}
                    description={service.description}
                  />
                ))}
              </ul>
            </ServiceDropdown>

            {/* Spray Tanning Dropdown */}
            <ServiceDropdown title="Spray Tanning">
              <ul className="space-y-4 text-lg">
                {services.sprayTanning.map((service) => (
                  <PriceRow
                    key={service.id}
                    name={service.name}
                    price={service.startingAt}
                    priceDisplay={service.priceDisplay}
                    description={service.description}
                  />
                ))}
              </ul>
            </ServiceDropdown>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Special Events */}
      <ChalkboardBackground
        as="section"
        backgroundImage={specialEventsBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20 border-y border-dusty-rose/10"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Special Events
            </h2>
            <p className="text-chalk/80 mb-8 max-w-lg mx-auto leading-relaxed">
              Everything else! Engagements, maternity, senior photos, or just a night out.
            </p>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
}
